import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiClient, type components } from '@/api/client';
import { patientQueries } from '@/features/patient-record/queries';
import {
  mapMeasurement,
  mapMedicalHistory,
} from '@/features/patient-record/mappers/medicalHistory';
import type {
  MeasurementQualifierViewModel,
  MeasurementType,
  MeasurementViewModel,
  MedicalHistoryViewModel,
} from '@/features/patient-record/types';

/**
 * Resource-level mutations for the patient's medical history section.
 * Bundles the narrative-text update with measurement add / update / delete
 * because they share the same domain and ship together — the narrative
 * card and the measurements table sit inside the same section component.
 *
 * Measurements are persisted as **date-scoped records** (one record per
 * visit) holding a `qualifiers[]` array (one entry per anthropometric
 * type at that date). Every write replaces the whole record; the
 * BE re-computes `valueSD` / `valuePercentile`.
 */

type MeasurementInputDto = components['schemas']['MeasurementInput'];
type MeasurementQualifierDto = components['schemas']['MeasurementQualifier'];

export interface UpdateNarrativeInput {
  readonly indicationForReferral: string | null;
  readonly medicalAndDevelopmentalHistory: string | null;
}

export interface MeasurementQualifierInput {
  readonly measurementType: MeasurementType;
  /** Value in centimeters (HEIGHT / HEAD_CIRCUMFERENCE). */
  readonly valueCm?: number | null;
  /** Value in kilograms (WEIGHT). */
  readonly valueKg?: number | null;
}

export interface MeasurementInput {
  /** ISO `YYYY-MM-DD`. Converted to the BE's partial-date shape on send. */
  readonly dateMeasured: string;
  readonly ageInMonths?: number | null;
  readonly notes?: string | null;
  readonly qualifiers: readonly MeasurementQualifierInput[];
}

export interface UpdateMeasurementInput {
  readonly id: string;
  readonly input: MeasurementInput;
}

interface NarrativeContext {
  readonly previous: MedicalHistoryViewModel | undefined;
}

interface MeasurementContext {
  readonly previous: MeasurementViewModel[] | undefined;
}

type UpdateNarrativeResult = UseMutationResult<
  MedicalHistoryViewModel,
  Error,
  UpdateNarrativeInput,
  NarrativeContext
>;
type ClearNarrativeResult = UseMutationResult<
  undefined,
  Error,
  void,
  NarrativeContext
>;
type AddMeasurementResult = UseMutationResult<
  MeasurementViewModel,
  Error,
  MeasurementInput,
  MeasurementContext
>;
type UpdateMeasurementResult = UseMutationResult<
  MeasurementViewModel,
  Error,
  UpdateMeasurementInput,
  MeasurementContext
>;
type DeleteMeasurementResult = UseMutationResult<
  undefined,
  Error,
  string,
  MeasurementContext
>;

export interface MedicalHistoryMutations {
  readonly updateNarrative: UpdateNarrativeResult;
  readonly clearNarrative: ClearNarrativeResult;
  readonly addMeasurement: AddMeasurementResult;
  readonly updateMeasurement: UpdateMeasurementResult;
  readonly deleteMeasurement: DeleteMeasurementResult;
}

function isoToPartialDate(iso: string): MeasurementInputDto['date'] {
  // `YYYY-MM-DD` → {year, month, day}. Defensive: if the string is
  // malformed, fall back to 0s so the BE can reject rather than the FE
  // silently lying. Day/month of 0 already signals "unknown".
  const [yearStr, monthStr, dayStr] = iso.split('-');
  const year = Number.parseInt(yearStr ?? '', 10);
  const month = Number.parseInt(monthStr ?? '', 10);
  const day = Number.parseInt(dayStr ?? '', 10);
  return {
    year: Number.isFinite(year) ? year : 0,
    month: Number.isFinite(month) ? month : 0,
    day: Number.isFinite(day) ? day : 0,
  };
}

function toQualifierDto(q: MeasurementQualifierInput): MeasurementQualifierDto {
  const dto: MeasurementQualifierDto = { measurementType: q.measurementType };
  if (q.valueCm !== undefined && q.valueCm !== null) dto.valueCm = q.valueCm;
  if (q.valueKg !== undefined && q.valueKg !== null) dto.valueKG = q.valueKg;
  return dto;
}

function toMeasurementInputDto(input: MeasurementInput): MeasurementInputDto {
  // BE requires `ageInMonths` to be non-null. Callers normally pre-compute
  // it from DOB + measurement date; if absent we send 0 ("unknown")
  // rather than letting validation fail.
  return {
    date: isoToPartialDate(input.dateMeasured),
    ageInMonths: input.ageInMonths ?? 0,
    ...(input.notes !== undefined && input.notes !== null ? { notes: input.notes } : {}),
    measurementQualifiers: input.qualifiers.map(toQualifierDto),
  };
}

function optimisticMeasurement(
  input: MeasurementInput,
  id: string,
): MeasurementViewModel {
  const qualifiers: MeasurementQualifierViewModel[] = input.qualifiers.map((q) => ({
    measurementType: q.measurementType,
    valueCm: q.valueCm ?? null,
    valueKg: q.valueKg ?? null,
    valueSD: null,
    valuePercentile: null,
  }));
  const partial = isoToPartialDate(input.dateMeasured);
  return {
    id,
    dateMeasured: input.dateMeasured,
    datePartial: {
      day: partial.day ?? 0,
      month: partial.month ?? 0,
      year: partial.year ?? 0,
    },
    ageInMonths: input.ageInMonths ?? null,
    notes: input.notes ?? null,
    qualifiers,
    source: null,
  };
}

export function useMedicalHistoryMutations(
  guid: string,
): MedicalHistoryMutations {
  const queryClient = useQueryClient();
  const narrativeKey = patientQueries.medicalHistory(guid).queryKey;
  const measurementsKey = patientQueries.measurements(guid).queryKey;

  const invalidateMeasurements = (): void => {
    queryClient
      .invalidateQueries({ queryKey: measurementsKey })
      .catch(() => undefined);
  };

  const invalidateNarrative = (): void => {
    queryClient
      .invalidateQueries({ queryKey: narrativeKey })
      .catch(() => undefined);
  };

  const snapshotNarrative = (): NarrativeContext => ({
    previous: queryClient.getQueryData(narrativeKey),
  });

  const updateNarrative = useMutation<
    MedicalHistoryViewModel,
    Error,
    UpdateNarrativeInput,
    NarrativeContext
  >({
    meta: { successMessage: 'success.toast.title.narrative_updated' },
    mutationFn: async (input) => {
      const dto = await apiClient.updateMedicalHistory(guid, {
        indicationForReferral: input.indicationForReferral ?? '',
        medicalAndDevelopmentalHistory:
          input.medicalAndDevelopmentalHistory ?? '',
      });
      return mapMedicalHistory(dto);
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: narrativeKey });
      const ctx = snapshotNarrative();
      queryClient.setQueryData(narrativeKey, {
        indicationForReferral: input.indicationForReferral,
        medicalAndDevelopmentalHistory: input.medicalAndDevelopmentalHistory,
      });
      return ctx;
    },
    onError: (_error, _input, context) => {
      if (context === undefined) return;
      queryClient.setQueryData(narrativeKey, context.previous);
    },
    onSettled: invalidateNarrative,
  });

  const clearNarrative = useMutation<undefined, Error, void, NarrativeContext>({
    meta: { successMessage: 'success.toast.title.narrative_cleared' },
    mutationFn: () => apiClient.clearMedicalHistory(guid),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: narrativeKey });
      const ctx = snapshotNarrative();
      queryClient.setQueryData(narrativeKey, {
        indicationForReferral: null,
        medicalAndDevelopmentalHistory: null,
      });
      return ctx;
    },
    onError: (_error, _input, context) => {
      if (context === undefined) return;
      queryClient.setQueryData(narrativeKey, context.previous);
    },
    onSettled: invalidateNarrative,
  });

  const snapshotMeasurements = (): MeasurementContext => ({
    previous: queryClient.getQueryData(measurementsKey),
  });

  const rollbackMeasurements = (context: MeasurementContext | undefined): void => {
    if (context === undefined) return;
    queryClient.setQueryData(measurementsKey, context.previous);
  };

  const addMeasurement = useMutation<
    MeasurementViewModel,
    Error,
    MeasurementInput,
    MeasurementContext
  >({
    meta: { successMessage: 'success.toast.title.measurement_created' },
    mutationFn: async (input) => {
      const dto = await apiClient.addMeasurement(guid, toMeasurementInputDto(input));
      return mapMeasurement(dto);
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: measurementsKey });
      const ctx = snapshotMeasurements();
      const optimistic = optimisticMeasurement(
        input,
        `optimistic-${Date.now().toString(36)}`,
      );
      queryClient.setQueryData(measurementsKey, [...(ctx.previous ?? []), optimistic]);
      return ctx;
    },
    onError: (_error, _input, context) => {
      rollbackMeasurements(context);
    },
    onSettled: invalidateMeasurements,
  });

  const updateMeasurement = useMutation<
    MeasurementViewModel,
    Error,
    UpdateMeasurementInput,
    MeasurementContext
  >({
    meta: { successMessage: 'success.toast.title.measurement_updated' },
    mutationFn: async ({ id, input }) => {
      // BE returns 200 with empty body on PUT — synthesize the next-VM
      // locally from the input so callers still get back a usable record.
      // The subsequent query invalidation pulls the BE-computed
      // valueSD / valuePercentile fields.
      await apiClient.updateMeasurement(guid, id, toMeasurementInputDto(input));
      return optimisticMeasurement(input, id);
    },
    onMutate: async ({ id, input }) => {
      await queryClient.cancelQueries({ queryKey: measurementsKey });
      const ctx = snapshotMeasurements();
      const next = optimisticMeasurement(input, id);
      queryClient.setQueryData(
        measurementsKey,
        (ctx.previous ?? []).map((m) => (m.id === id ? next : m)),
      );
      return ctx;
    },
    onError: (_error, _input, context) => {
      rollbackMeasurements(context);
    },
    onSettled: invalidateMeasurements,
  });

  const deleteMeasurement = useMutation<undefined, Error, string, MeasurementContext>({
    meta: { successMessage: 'success.toast.title.measurement_deleted' },
    mutationFn: (id) => apiClient.deleteMeasurement(guid, id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: measurementsKey });
      const ctx = snapshotMeasurements();
      queryClient.setQueryData(
        measurementsKey,
        (ctx.previous ?? []).filter((m) => m.id !== id),
      );
      return ctx;
    },
    onError: (_error, _input, context) => {
      rollbackMeasurements(context);
    },
    onSettled: invalidateMeasurements,
  });

  return {
    updateNarrative,
    clearNarrative,
    addMeasurement,
    updateMeasurement,
    deleteMeasurement,
  };
}
